import { currencyFormatter } from '@/helpers/currencyFormatter';
import { formatDate } from '@/helpers/dates';
import { Box, Column, Pagination, Table } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { useGetIncentiveAccountFiltersQuery, useLazySearchIncentiveAccountsQuery } from '../../services/incentives';
import { FilterBar } from '../filters';
import { AddFilterButtonDropdown } from '../filters/AddFilterButton';
import { AppliedDataFilter } from '../filters/FilterButton';
import { SkeletonBox } from '../SkeletonBox';

export const IncentiveAccountsTable = () => {
  const { key } = useParams<{ key: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [appliedFilters, setAppliedFilters] = useState<AppliedDataFilter[]>([]);

  const [trigger, { data, isLoading, isFetching }] = useLazySearchIncentiveAccountsQuery();
  const { data: filters = [], isLoading: isFiltersLoading } = useGetIncentiveAccountFiltersQuery({ key: key! });

  const pageNum = parseInt(searchParams.get('pageNum') || '1', 10);
  const pageSize = 20;
  const searchTerm = searchParams.get('searchTerm') || undefined;
  const advancedFilters = searchParams.get('advancedFilters') || undefined;

  useEffect(() => {
    let filtersFromUrl = advancedFilters;
    let parsedFilters: AppliedDataFilter[] = [];

    if (filtersFromUrl) {
      try {
        parsedFilters = JSON.parse(filtersFromUrl) as AppliedDataFilter[];
      } catch {
        parsedFilters = [];
        filtersFromUrl = JSON.stringify([]);
      }
    } else {
      parsedFilters = [];
      filtersFromUrl = JSON.stringify([]);
    }

    setAppliedFilters(parsedFilters);
    trigger({
      key: key!,
      searchTerm,
      pageNum,
      pageSize,
      advancedFilters: filtersFromUrl,
    });
  }, [trigger, key, searchTerm, pageNum, advancedFilters]);

  const handleApplyAdvancedFilters = useCallback(
    (filtersToApply: AppliedDataFilter[]) => {
      if (filtersToApply.some((filter) => filter.selectedValues && filter.selectedValues.length)) {
        searchParams.set('advancedFilters', JSON.stringify(filtersToApply));
      }
      searchParams.delete('pageNum');
      setSearchParams(searchParams);
      setAppliedFilters(filtersToApply);
    },
    [searchParams, setSearchParams],
  );

  const handleRemoveAdvancedFilter = useCallback(
    (filterId: string) => {
      const newFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length) {
        searchParams.set('advancedFilters', JSON.stringify(newFilters));
      } else {
        searchParams.delete('advancedFilters');
      }
      searchParams.delete('pageNum');
      setSearchParams(searchParams);
      setAppliedFilters(newFilters);
    },
    [appliedFilters, searchParams, setSearchParams],
  );

  const handleClearAdvancedFilters = useCallback(() => {
    searchParams.delete('advancedFilters');
    searchParams.delete('pageNum');
    setSearchParams(searchParams);
    setAppliedFilters([]);
  }, [searchParams, setSearchParams]);

  const handlePageChange = (newPage: number) => {
    searchParams.set('pageNum', newPage.toString());
    setSearchParams(searchParams);
  };

  const handleSearchChange = (newSearchTerm: string) => {
    if (newSearchTerm && newSearchTerm !== '') {
      searchParams.set('searchTerm', newSearchTerm);
    } else {
      searchParams.delete('searchTerm');
    }
    searchParams.delete('pageNum');
    setSearchParams(searchParams);
  };

  const columns: Column[] = [
    {
      heading: 'Account',
      render: (_cell: any, row: any) => (
        <Link to={`/accounts/${row._id}`} style={{ textDecoration: 'none' }}>
          <Box>
            <Box as="span" color="primary" fontWeight="medium">
              {row.primaryApplicantName || 'N/A'}
            </Box>
            {row.address && (
              <Box fontSize="xs" color="body-secondary">
                {[row.address.address1, row.address.city, row.address.state].filter(Boolean).join(', ')}
              </Box>
            )}
          </Box>
        </Link>
      ),
    },
    {
      heading: 'Allocation',
      render: (_cell: any, row: any) => {
        const allocations = row.allocations || [];
        if (allocations.length === 0) {
          return <Box fontSize="sm">N/A</Box>;
        }
        return (
          <Box fontSize="sm">
            {allocations.map((alloc: { allocationId: string; allocationName: string }) => (
              <Box key={alloc.allocationId}>{alloc.allocationName}</Box>
            ))}
          </Box>
        );
      },
    },
    {
      heading: 'Date Assigned',
      render: (_cell: any, row: any) => {
        const allocations = row.allocations || [];
        if (allocations.length === 0) {
          return <Box fontSize="sm">N/A</Box>;
        }
        return (
          <Box fontSize="sm">
            {allocations.map((alloc: { allocationId: string; dateAssigned?: string | Date }) => (
              <Box key={alloc.allocationId}>{formatDate(alloc.dateAssigned)}</Box>
            ))}
          </Box>
        );
      },
    },
    {
      heading: 'System Size',
      render: (_cell: any, row: any) => (
        <Box fontSize="sm">{row.systemSizeKw ? `${row.systemSizeKw} kW` : 'N/A'}</Box>
      ),
    },
    {
      heading: 'System Cost',
      render: (_cell: any, row: any) => (
        <Box fontSize="sm">{row.totalSystemCost ? currencyFormatter.format(row.totalSystemCost) : 'N/A'}</Box>
      ),
    },
  ];

  const totalPages = data ? Math.ceil(data.totalCount / pageSize) : 0;

  return (
    <Box childGap="md">
      <Box as="h3" fontWeight="medium" fontSize="md">
        Accounts ({data?.totalCount ?? '-'})
      </Box>

      <FilterBar
        filtersState={{ searchTerm }}
        handleSearchTerm={handleSearchChange}
        placeholder="Search accounts..."
      >
        {!isFiltersLoading && filters.length > 0 && (
          <AddFilterButtonDropdown
            dataFilters={filters}
            appliedFilters={appliedFilters}
            onRemoveFilter={handleRemoveAdvancedFilter}
            onApplyFilters={handleApplyAdvancedFilters}
            onClearFilters={handleClearAdvancedFilters}
          />
        )}
      </FilterBar>

      {isFetching || isLoading ? (
        <Box childGap="sm">
          <SkeletonBox height="48px" width="100%" />
          <SkeletonBox height="48px" width="100%" />
          <SkeletonBox height="48px" width="100%" />
          <SkeletonBox height="48px" width="100%" />
          <SkeletonBox height="48px" width="100%" />
        </Box>
      ) : !data?.accounts?.length ? (
        <Box padding="3xl" textAlign="center" color="body-secondary" background="secondary" radius="md">
          <Box fontSize="md" fontWeight="medium" margin="0 0 xs 0">
            No accounts found
          </Box>
          <Box fontSize="sm">
            {searchTerm || advancedFilters
              ? 'Try adjusting your search or filter criteria.'
              : 'Accounts assigned to this incentive will appear here.'}
          </Box>
        </Box>
      ) : (
        <>
          <Table rowKey="_id" columns={columns} rows={data.accounts} isScrollable={{ x: true }} />

          {totalPages > 1 && (
            <Box direction="row" justifyContent="center" margin="md 0 0 0">
              <Pagination
                activePage={pageNum}
                numberOfPagesDisplayed={5}
                onChange={handlePageChange}
                totalItemsCount={data.totalCount}
                itemsPerPage={pageSize}
                arePagesVisible
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
